/**
 * Round to 2 decimal places (paise) using standard math rounding.
 *
 * @param {number|string} amount
 * @returns {number}
 */
export function toMoney(amount) {
  return Math.round((Number(parseFloat(amount)) || 0) * 100) / 100;
}

/**
 * Auto round off to the nearest whole number using standard math rounding.
 *
 * @param {number|string} amount
 * @returns {{ original: number, rounded: number, roundOff: number }}
 */
export function autoRoundOff(amount) {
  const original = Number(parseFloat(amount)) || 0;
  const rounded = Math.round(original);
  const roundOff = Math.round((rounded - original) * 100) / 100;

  return { original, rounded, roundOff };
}

/**
 * Format the round-off delta for compact UI display (e.g. "+0.10", "-0.32").
 *
 * @param {number} roundOff
 * @returns {string|null}
 */
export function formatAutoRoundOffIndicator(roundOff) {
  if (!roundOff) return null;

  const sign = roundOff > 0 ? "+" : "";
  return `${sign}${roundOff.toFixed(2)}`;
}

/**
 * Round a line TotalAmt to the nearest whole rupee (35719.40 → 35719.00).
 *
 * @param {number|string} amount
 * @returns {string}
 */
export function roundLineTotalAmount(amount) {
  return Math.round(Number(parseFloat(amount)) || 0).toFixed(2);
}

/**
 * Label for bill / payment round-off row.
 * Positive adjustment → ROUND UP, negative → ROUND DOWN.
 *
 * @param {number} roundOff
 * @returns {"ROUND UP"|"ROUND DOWN"|"ROUND OFF"}
 */
export function formatRoundOffLabel(roundOff) {
  if (roundOff > 0) return "ROUND UP";
  if (roundOff < 0) return "ROUND DOWN";
  return "ROUND OFF";
}

/**
 * Bill tax footer: money-round GST lines, then auto round-off so
 * total − discount + GST + roundOff lands on a whole-rupee amount after tax.
 * Do not use backend round_off — it often drifts from displayed GST paise.
 *
 * @param {{
 *   totalBeforeTax?: number|string,
 *   discountAmount?: number|string,
 *   sgst?: number|string,
 *   cgst?: number|string,
 *   igst?: number|string,
 * }} params
 */
export function computeBillTaxRoundOff({
  totalBeforeTax = 0,
  discountAmount = 0,
  sgst = 0,
  cgst = 0,
  igst = 0,
} = {}) {
  const displaySgst = toMoney(sgst);
  const displayCgst = toMoney(cgst);
  const displayIgst = toMoney(igst);
  const amountBeforeRoundOff =
    toMoney(totalBeforeTax) -
    toMoney(discountAmount) +
    displaySgst +
    displayCgst +
    displayIgst;
  const { rounded, roundOff } = autoRoundOff(amountBeforeRoundOff);

  return {
    displaySgst,
    displayCgst,
    displayIgst,
    roundOff,
    amountAfterTax: rounded,
    roundOffLabel: formatRoundOffLabel(roundOff),
  };
}
